import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { getImpersonationHeaders } from "./impersonation";

const CSRF_COOKIE_NAME = "XSRF-TOKEN";
const CSRF_HEADER_NAME = "X-CSRF-Token";
const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

// Read the non-HttpOnly XSRF-TOKEN cookie our server sets via the
// double-submit-cookie CSRF middleware (see server/middleware/csrf.ts).
export function readCsrfCookie(): string | null {
  if (typeof document === "undefined") return null;
  const raw = document.cookie || "";
  const m = raw.match(new RegExp(`(?:^|; )${CSRF_COOKIE_NAME}=([^;]+)`));
  return m ? decodeURIComponent(m[1]) : null;
}

// Bootstrap the CSRF cookie when we don't yet have one. The /api/csrf
// endpoint is GET-only and unauthenticated — it just reads-or-creates the
// XSRF-TOKEN cookie so a follow-up mutating request can echo it back.
//
// In-flight de-dupe so a burst of mutations on a fresh page (e.g. a login
// click that fires three queued mutations) only triggers one bootstrap.
let csrfBootstrapInFlight: Promise<string | null> | null = null;
export async function bootstrapCsrfToken(): Promise<string | null> {
  const existing = readCsrfCookie();
  if (existing) return existing;
  if (csrfBootstrapInFlight) return csrfBootstrapInFlight;
  csrfBootstrapInFlight = (async () => {
    try {
      await fetch("/api/csrf", { credentials: "include" });
    } catch {
      // Network errors here surface on the actual mutating request below;
      // no need to throw twice.
    }
    return readCsrfCookie();
  })().finally(() => {
    csrfBootstrapInFlight = null;
  });
  return csrfBootstrapInFlight;
}

/**
 * Returns headers carrying the X-CSRF-Token, bootstrapping the cookie
 * first if needed. Call sites that need to issue a raw `fetch` (instead
 * of going through `apiRequest`) — e.g. the login form, which wants to
 * read the response body even on 4xx without `apiRequest`'s throw-on-not-ok
 * behavior — should spread the result into their fetch headers.
 */
export async function withCsrfHeader(
  base: Record<string, string> = {},
): Promise<Record<string, string>> {
  let token = readCsrfCookie();
  if (!token) token = await bootstrapCsrfToken();
  return token ? { ...base, "X-CSRF-Token": token } : { ...base };
}

// Billing suspension intercept. The workspaceContext middleware returns 402
// `{ code: "BILLING_SUSPENDED" }` for every non-exempt API call once a
// workspace's plan is unpaid past the grace window. We broadcast a window event
// so the global <BillingSuspendedGate> can render a full-screen paywall, no
// matter which query/mutation tripped it.
let billingSuspendedBroadcast = false;
async function maybeInterceptBillingSuspended(res: Response): Promise<void> {
  if (res.status !== 402) return;
  // Platform Admin routes are never tenant-workspace-scoped — a suspended
  // tenant workspace must never block the platform surface. Shared components
  // (GmailReconnectPrompt, ServerUnavailableBanner, etc.) mount on every page
  // including /platform/* and can 402 because their API calls carry the
  // suspended workspace session. We suppress the broadcast on platform routes
  // so the gate never fires there.
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/platform")) return;
  try {
    const body = await res.clone().json();
    if (body?.code === "BILLING_SUSPENDED" || body?.billingSuspended === true) {
      if (typeof window !== "undefined" && !billingSuspendedBroadcast) {
        billingSuspendedBroadcast = true;
        window.dispatchEvent(new CustomEvent("billing-suspended"));
      }
    }
  } catch {
    /* not JSON — ignore */
  }
}

// SESSION_EXPIRED intercept. The workspaceContext middleware returns 401
// `{ code: "SESSION_EXPIRED" }` when there is no req.user on the request.
// Rather than silently toasting "Update Failed" or "Failed to load" on
// every request, redirect to /login so the user can re-authenticate. We
// track whether a redirect is already in-flight so parallel failing
// requests don't fire multiple window.location assignments.
let sessionExpiredRedirectInFlight = false;

// Task #576 — re-verify before yanking the user to the login screen.
//
// A single transient "no user" blip (the OIDC token-refresh race, or a
// multi-second event-loop-lag spike that momentarily stalls the session
// store read) can make ONE request 401 with SESSION_EXPIRED even though
// the session is perfectly valid a moment before and after. Hard-
// redirecting on that single blip is exactly what bounces logged-in users
// to the Replit consent screen on a routine refresh. So before navigating
// away we re-check `/api/auth/user`: if it confirms the session is still
// alive, we swallow the blip and let the page keep working. Only a
// confirmed-dead session triggers the redirect.
//
// The re-check is de-duped so a burst of parallel 401s only fires one
// verification request.
let sessionReverifyInFlight: Promise<boolean> | null = null;
async function sessionStillValid(): Promise<boolean> {
  if (sessionReverifyInFlight) return sessionReverifyInFlight;
  sessionReverifyInFlight = (async () => {
    // Two quick attempts: the first refresh of the rotating OIDC token may
    // itself land on the in-flight refresh; a short retry lets it settle.
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const res = await fetch("/api/auth/user", {
          credentials: "include",
          headers: getImpersonationHeaders(),
        });
        if (res.ok) return true;
        // 401/403 here means genuinely no session — stop retrying.
        if (res.status === 401 || res.status === 403) return false;
      } catch {
        // network error — fall through to retry
      }
      if (attempt === 0) await new Promise((r) => setTimeout(r, 350));
    }
    return false;
  })().finally(() => {
    sessionReverifyInFlight = null;
  });
  return sessionReverifyInFlight;
}

// Public-route prefixes that do not require authentication. A SESSION_EXPIRED
// 401 from an API call made by a globally-mounted component (e.g.
// GmailReconnectPrompt) while the user is on one of these pages must NOT
// trigger a login redirect — the page itself is intentionally unauthenticated.
// Redirecting from /login creates the infinite returnTo nesting loop; redirecting
// from /invoice/ (or other token-gated pages) is the primary bug this guards.
const PUBLIC_PATH_PREFIXES = [
  "/login",
  "/invoice/",
  "/sign/",
  "/portal/",
  "/artist/",
  "/request-event",
  "/request-show",
  "/book/",
  "/review/",
  "/share/",
  "/signup",
  "/verify-email",
  "/forgot-password",
  "/reset-password",
  "/first-login",
  "/beta",
  "/welcome",
  "/pricing",
  "/marketing/",
  "/privacy",
  "/terms",
  "/data-deletion",
  "/help",
  "/security",
  "/trust",
  "/policies",
  "/connect-sms/",
  "/connect-calendar/",
  "/platform/login",
  "/mfa-challenge",
];

function isPublicClientPath(path: string): boolean {
  return PUBLIC_PATH_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(prefix + "?") || path.startsWith(prefix + "/") || path === prefix.replace(/\/$/, ""),
  );
}

async function maybeInterceptSessionExpired(res: Response): Promise<boolean> {
  if (res.status !== 401) return false;
  try {
    const body = await res.clone().json();
    if (body?.code !== "SESSION_EXPIRED") return false;
  } catch {
    return false;
  }
  // Never redirect from public / unauthenticated pages. An API 401 that
  // arrives from a globally-mounted component (e.g. GmailReconnectPrompt
  // querying /api/email-drafts/pending-count) while the user is on a public
  // page should be silently swallowed — the page doesn't require a session.
  // Without this guard, visiting /invoice/<token> unauthenticated causes an
  // infinite /login?returnTo=/login?returnTo=... nesting loop.
  if (typeof window !== "undefined" && isPublicClientPath(window.location.pathname)) {
    return false;
  }
  // If a redirect is already committed, don't bother re-verifying.
  if (sessionExpiredRedirectInFlight) return true;
  // Re-verify: a single transient blip should not log the user out.
  if (await sessionStillValid()) {
    // Session is actually fine — let the caller's normal error handling run
    // for just this one request. No logout, no redirect, no consent screen.
    return false;
  }
  if (typeof window !== "undefined" && !sessionExpiredRedirectInFlight) {
    sessionExpiredRedirectInFlight = true;
    const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
    // Route through the /login client page (NOT a consent-forcing
    // /api/login). That page re-checks the session and, for a still-valid
    // session, forwards straight back to returnTo; for a genuinely expired
    // OIDC session it offers a consent-free "Sign in with Replit" path.
    window.location.href = `/login?returnTo=${returnTo}`;
  }
  return true; // caller should not throw — redirect is handling it
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    if (res.status === 401) {
      const redirected = await maybeInterceptSessionExpired(res);
      if (redirected) {
        // Swallow the error — the page is navigating to login.
        throw new Error("SESSION_EXPIRED");
      }
    }
    if (res.status === 402) {
      await maybeInterceptBillingSuspended(res);
    }
    // MFA gate intercept — server returns 403 with reason 'verification_required'
    // (user enrolled but session not verified) or 'enrollment_required'.
    if (res.status === 403) {
      const cloned = res.clone();
      try {
        const body = await cloned.json();
        if (body?.reason === "verification_required" || body?.reason === "enrollment_required") {
          if (typeof window !== "undefined") {
            const path = window.location.pathname;
            const target = body.reason === "enrollment_required" ? "/account/security" : "/mfa-challenge";
            if (!path.startsWith(target) && !path.startsWith("/account/security") && !path.startsWith("/mfa-challenge")) {
              const next = encodeURIComponent(path + window.location.search);
              window.location.href = `${target}?next=${next}`;
            }
          }
        }
      } catch { /* not JSON — fall through */ }
    }
    // Standardised rate-limit envelope from server/middleware/rate-limit.ts
    // looks like `{ error: "rate_limited", retryAfterSec, message }`. Surface
    // just the human message so toasts read cleanly instead of dumping JSON.
    if (res.status === 429) {
      const cloned = res.clone();
      try {
        const body = await cloned.json();
        if (body && typeof body.message === "string" && body.message.length > 0) {
          throw new Error(`429: ${body.message}`);
        }
      } catch (parseErr) {
        // If we already produced our own Error above, rethrow it.
        if (parseErr instanceof Error && parseErr.message.startsWith("429: ")) {
          throw parseErr;
        }
        // else fall through to generic text path below
      }
    }
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${sanitizeErrorBody(text, res.status)}`);
  }
}

// Some upstreams (notably Google's GFE returning a 401/403/5xx) respond with a
// full HTML error page instead of JSON. If that raw HTML reaches a destructive
// toast it shows the user `<!doctype html>...<title>403</title>403 Forbidden`,
// which is meaningless and alarming. Detect HTML-ish bodies anywhere in the
// stack (this is the single chokepoint every apiRequest + query error flows
// through) and replace them with a clean, status-appropriate message.
export function looksLikeHtml(text: string): boolean {
  if (!text) return false;
  const head = text.trimStart().slice(0, 200).toLowerCase();
  return (
    head.startsWith("<!doctype") ||
    head.startsWith("<html") ||
    head.startsWith("<head") ||
    head.startsWith("<meta") ||
    (head.includes("<title>") && head.includes("</title>")) ||
    /<\/?(html|head|body|meta|title)\b/i.test(head)
  );
}

export function sanitizeErrorBody(text: string, status: number): string {
  if (!looksLikeHtml(text)) return text;
  // HTML body — almost always an upstream auth/permission/availability page.
  if (status === 401 || status === 403) {
    return "Email authorization was rejected — your mailbox connection may need to be reconnected.";
  }
  if (status >= 500) {
    return "The email service is temporarily unavailable. Please try again.";
  }
  return "The request could not be completed.";
}

// Mutations that count toward gamification metrics. When any of these succeed
// we invalidate the snapshot so the global celebration listener can detect
// any goal that was just crossed — no matter where the user happens to be in
// the app.
const GAMIFICATION_INVALIDATING_PATTERNS: RegExp[] = [
  /^\/api\/leads(\b|\/|\?)/,
  /^\/api\/clients(\b|\/|\?)/,
  /^\/api\/bookings(\b|\/|\?)/,
  /^\/api\/email-hub(\b|\/|\?)/,
];

function shouldInvalidateGamification(method: string, url: string): boolean {
  const m = method.toUpperCase();
  if (m === "GET" || m === "HEAD" || m === "OPTIONS") return false;
  // Strip query string for pattern matching
  const path = url.split("?")[0];
  return GAMIFICATION_INVALIDATING_PATTERNS.some((re) => re.test(path));
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const impersonationHeaders = getImpersonationHeaders();
  const headers: Record<string, string> = { ...impersonationHeaders };
  if (data) {
    headers["Content-Type"] = "application/json";
  }

  // CSRF: every non-safe method must echo the XSRF-TOKEN cookie back as
  // the X-CSRF-Token header. Bootstrap the cookie if it doesn't exist yet
  // (first mutation from a brand-new tab).
  const upperMethod = method.toUpperCase();
  if (!SAFE_METHODS.has(upperMethod)) {
    let token = readCsrfCookie();
    if (!token) token = await bootstrapCsrfToken();
    if (token) headers[CSRF_HEADER_NAME] = token;
  }

  const res = await fetch(url, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res);

  if (shouldInvalidateGamification(method, url)) {
    queryClient.invalidateQueries({ queryKey: ["/api/gamification/snapshot"] });
  }

  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const impersonationHeaders = getImpersonationHeaders();
    
    const res = await fetch(queryKey.join("/") as string, {
      credentials: "include",
      headers: impersonationHeaders,
    });

    if (res.status === 401) {
      const redirected = await maybeInterceptSessionExpired(res);
      if (redirected) throw new Error("SESSION_EXPIRED");
      if (unauthorizedBehavior === "returnNull") return null;
    }

    if (res.status === 402) {
      await maybeInterceptBillingSuspended(res);
    }

    if (res.status === 403) {
      try {
        const cloned = res.clone();
        const body = await cloned.json();
        if (body?.reason === "mfa_required" && typeof window !== "undefined" && !window.location.pathname.startsWith("/mfa-challenge")) {
          const next = encodeURIComponent(window.location.pathname + window.location.search);
          window.location.href = `/mfa-challenge?next=${next}`;
        }
      } catch { /* not JSON */ }
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

// Retry a mutation only for transient failures — network errors and 5xx
// server errors. Never retry client errors (4xx except 408 timeout and
// 429 rate-limit which can recover) because those won't improve on
// re-submission and could cause unintended duplicate side-effects.
function isMutationRetryable(error: unknown): boolean {
  if (!(error instanceof Error)) return true; // unknown shape — optimistic retry
  const msg = error.message;
  const codeMatch = msg.match(/^(\d{3}):/);
  if (!codeMatch) return true; // network / fetch error — always retry
  const status = Number(codeMatch[1]);
  if (status === 408 || status === 429) return true; // timeout / rate-limit
  if (status >= 400 && status < 500) return false;   // other 4xx — don't retry
  return true; // 5xx and anything else
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: (failureCount, error) =>
        failureCount < 2 && isMutationRetryable(error),
      retryDelay: (attempt) => Math.min(800 * 2 ** attempt, 5000),
    },
  },
});
